import { computed, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DialogOptions, DialogService, ModalComponent, ModalData } from 'desy-angular';
import { Observable, of, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GlobalServices } from '../global/global.service';
import { UserAnjana } from '../../models/core/auth.models';
import { LocalStorageKeys } from '../../models/core/storage.models';
import { MainRoutes } from '../../models/core/routes.models';

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends GlobalServices {
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly document = inject(DOCUMENT);

  public static readonly User = signal<UserAnjana>(null);
  public static readonly UserCompleteName = computed(() => {
    const user = AuthenticationService.User();
    if (!user) return '';
    const isValid = (value: string | undefined): boolean => !!value && value !== 'null' && value !== '';
    return [user?.givenName, user?.surname1, user?.surname2]
      .filter(isValid)
      .map((name) => name.trim())
      .join(' ')
      .trim();
  });

  logout(): void {
    const identificationMethod = AuthenticationService.User()?.identificationMethod;
    this.handleLogout();
    const redirectUrl = btoa(`${this.document.location.origin}/${MainRoutes.LOGIN}`);
    this.document.location.href = `${environment.integration_url}/mfe_core/rest/identification/HAL/${redirectUrl}/${identificationMethod}/logout`;
  }

  logoutUser(): Observable<boolean> {
    if (!this.inLoginPage()) {
      this.openDialog();
    }
    return of(false);
  }

  getLocalStorageUser(): UserAnjana {
    const user = localStorage.getItem(LocalStorageKeys.CURRENT_USER);
    return user ? JSON.parse(user) : null;
  }

  private openDialog(): void {
    const dialogOptions: DialogOptions = {
      id: 'expired-token-dialog',
      dismissOnBackdrop: false
    };

    void this.dialogService.openDialog(ModalComponent, dialogOptions).then((result) => {
      const component = result.component.instance as ModalComponent;

      component.titleModal = {
        text: 'Sesión expirada',
        html: null,
        classes: ''
      };

      component.description = {
        html: '<p>Su sesión ha finalizado por inactividad</p>'
      } as ModalData;

      component.itemsPrimary = [
        {
          text: 'VOLVER A IDENTIFICARME',
          classes: 'c-button--primary'
        }
      ];

      component.isDismissible = false;
      component.id = 'modal';
      component.classes = 'relative max-w-lg mx-auto p-base lg:p-lg border border-neutral-base rounded bg-white';
      component.role = 'dialog';
      component.ariaModal = 'true';

      component.clickButton.pipe(take(1)).subscribe(() => {
        this.dialogService.closeDialog(result.dialog);
        this.logout();
      });
    });
  }

  private inLoginPage(): boolean {
    return this.router.url.indexOf('login') > -1;
  }

  /**
   * Nos aseguramos de que no hay espacios delante, ni detrás, y
   * si hay varios espacios juntos, que se reemplacen por espacio único
   * @returns The cleaned up string.
   */
  getUserName(): string {
    const user: UserAnjana = AuthenticationService.User();
    let userName: string = '';
    if (user?.givenName && user.givenName !== 'null' && user.givenName !== '') {
      userName = ` ${user.givenName}`;
    }
    if (user?.surname1 && user.surname1 !== 'null' && user.surname1 !== '') {
      userName += ` ${user.surname1}`;
    }
    if (user?.surname2 && user.surname2 !== 'null' && user.surname2 !== '') {
      userName += ` ${user.surname2}`;
    }

    return userName.replace(/^\s+/gi, '').replace(/\s+$/gi, '').replace(/\s+/gi, ' ');
  }

  handleLogout(): void {
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      this.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
    localStorage.clear();
    sessionStorage.clear();
    AuthenticationService.User.set(null);
    void this.router.navigate([MainRoutes.LOGIN]);
  }
}
